"use client";

import PagedCloudImage from "@/app/_lib/components/cloudinary/paged-cloud-image";
import { DocumentTypes, Document } from "@/prisma-package/src";
import SelectInput from "@/app/_lib/components/forms/select";
import { Fragment, useState, useEffect } from "react";
import {
  updateDocumentsAction,
  getDocumentWithVendorsAction,
  updateDocumentExtractedDataAction,
  updateDocumentVendorAssociationAction
} from "@/app/_lib/actions/document.actions";
import { getAllVendorsForProjectDropdownAction } from "@/app/_lib/actions/vendor.actions";
import Modal from "@/app/_lib/components/modals/modal";
import { useToast } from "@/app/_lib/hooks/use-toast";
import { getEstDateTime } from "@/app/_lib/utils/format-date-time.utils";
import { matchVendorCandidates } from "@/app/_lib/utils/document-extraction.utils";
import {
  humanizeFieldKey,
  mergeExtractedData,
} from "@/app/_lib/utils/extraction-fields.utils";
import { useDocumentTypeDefinitions } from "@/app/_lib/hooks/use-document-type-definitions";
import {
  categoryForType,
  typesForCategory,
} from "@/app/_lib/utils/document-category.utils";
import { Vendor } from "@/prisma-package/src";

type Props = {
  onClose: () => void;
  src: string;
  alt: string;
  width: number;
  height: number;
  sizes?: string;
  crop?: string;
  className?: string;
  documentType?: string;
  projectId?: string;
  documentId?: string;
  allowedDocumentTypes?: Record<string, string>;
  isOpen: boolean;
  viewOnly?: boolean;
  vendors?: Vendor[];
  initialDocumentData?: Document & {
    extractedVendors?: Array<{
      vendor: { id: string; name: string };
      confidence: number;
    }>;
  };
};

const DocumentViewModal: React.FC<Props> = ({
  src,
  alt,
  sizes,
  className,
  onClose,
  documentType,
  projectId,
  documentId,
  allowedDocumentTypes,
  isOpen,
  viewOnly = false,
  vendors,
  initialDocumentData,
}) => {
  const toast = useToast();
  const { definitions, labelFor } = useDocumentTypeDefinitions(projectId);
  const [currentDocumentType, setCurrentDocumentType] = useState(documentType);
  const [documentData, setDocumentData] = useState<Document & {
    extractedVendors?: Array<{
      vendor: { id: string; name: string };
      confidence: number;
    }>;
  } | null>(initialDocumentData || null);
  const [isLoading, setIsLoading] = useState(!initialDocumentData); // Only load if no initial data
  const [isEditingDocId, setIsEditingDocId] = useState(false);
  const [editedDocId, setEditedDocId] = useState(initialDocumentData?.extractedDocumentId || "");
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(
    initialDocumentData?.extractedVendors?.[0]?.vendor.id || null
  );
  const [vendorsList, setVendorsList] = useState<Vendor[]>(vendors || []);
  const [isAssigningVendor, setIsAssigningVendor] = useState(false);
  // Custom extraction field being edited inline (one at a time, like the
  // Document ID) and its draft value.
  const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
  const [editedFieldValue, setEditedFieldValue] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const type = formData.get("documentType") as string;

    const docData = {
      type,
      projectId: projectId || "",
      documentId: documentId || "",
    };

    const [error] = await updateDocumentsAction(docData);

    if (error) {
      console.error(error.message);
      toast({
        message: error.message,
        type: "error",
        state: "active",
      });
    } else {
      toast({
        message: "Document type updated successfully",
        type: "success",
        state: "active",
      });
      onClose();
    }
  };

  // Update state when initialDocumentData changes
  useEffect(() => {
    if (initialDocumentData) {
      setDocumentData(initialDocumentData);
      setEditedDocId(initialDocumentData.extractedDocumentId || "");
      setSelectedVendorId(initialDocumentData.extractedVendors?.[0]?.vendor.id || null);
      setIsLoading(false);
    }
  }, [initialDocumentData]);

  // Update vendors when vendors prop changes
  useEffect(() => {
    if (vendors && vendors.length > 0) {
      setVendorsList(vendors);
    }
  }, [vendors]);

  useEffect(() => {
    const fetchDocumentData = async () => {
      // If we have initial data and vendors, no need to fetch
      if (initialDocumentData && vendors) {
        return;
      }

      if (documentId && isOpen) {
        setIsLoading(true);
        try {
          // Only fetch what we don't already have
          const promises = [];
          if (!initialDocumentData) {
            promises.push(getDocumentWithVendorsAction({ documentId }));
          }
          if (!vendors && projectId) {
            promises.push(getAllVendorsForProjectDropdownAction({ projectId }));
          }

          const results = await Promise.all(promises);
          
          let resultIndex = 0;
          if (!initialDocumentData && results[resultIndex]) {
            const [docError, docData] = results[resultIndex];
            if (!docError && docData && 'id' in docData && 'publicId' in docData) {
              setDocumentData(docData);
              setEditedDocId(docData.extractedDocumentId || "");
              // Set the current vendor if one exists
              if (docData.extractedVendors && docData.extractedVendors.length > 0) {
                setSelectedVendorId(docData.extractedVendors[0].vendor.id);
              }
            }
            resultIndex++;
          }
          
          if (!vendors && results[resultIndex]) {
            const [vendorError, vendorData] = results[resultIndex];
            if (!vendorError && vendorData && 'vendors' in vendorData) {
              setVendorsList(vendorData.vendors || []);
            }
          }
        } catch (err) {
          console.error("Failed to fetch document data:", err);
        } finally {
          setIsLoading(false);
        }
      } else if (!isOpen) {
        // Reset state when modal closes, but keep pre-loaded data
        if (!initialDocumentData) {
          setDocumentData(null);
        }
        setIsLoading(!initialDocumentData);
        setIsEditingDocId(false);
        setIsEditingVendor(false);
      }
    };

    fetchDocumentData();
  }, [documentId, isOpen, projectId, initialDocumentData, vendors]);

  const handleSaveDocId = async () => {
    if (!documentId) return;
    
    const [error] = await updateDocumentExtractedDataAction({
      documentId,
      extractedDocumentId: editedDocId || null,
    });

    if (error) {
      toast({
        message: "Failed to update document ID",
        type: "error",
        state: "active",
      });
    } else {
      toast({
        message: "Document ID updated successfully",
        type: "success",
        state: "active",
      });
      setIsEditingDocId(false);
      if (documentData) {
        setDocumentData({
          ...documentData,
          extractedDocumentId: editedDocId || null,
        });
      }
    }
  };

  const assignVendor = async (vendorId: string | null) => {
    if (!documentId || isAssigningVendor) return;
    setIsAssigningVendor(true);

    const [error] = await updateDocumentVendorAssociationAction({
      documentId,
      vendorId,
      confidence: 1.0, // 100% confidence for manual selection
    });

    if (error) {
      toast({
        message: "Failed to update vendor",
        type: "error",
        state: "active",
      });
    } else {
      toast({
        message: "Vendor updated successfully",
        type: "success",
        state: "active",
      });
      setIsEditingVendor(false);
      setSelectedVendorId(vendorId);

      // Refresh document data to show updated vendor
      const [refreshError, refreshedData] = await getDocumentWithVendorsAction({ documentId });
      if (!refreshError && refreshedData) {
        setDocumentData(refreshedData as any);
      }
    }

    setIsAssigningVendor(false);
  };

  const handleSaveVendor = () => assignVendor(selectedVendorId);

  const handleSaveField = async (key: string) => {
    if (!documentId) return;

    const value = editedFieldValue.trim();
    // The action merges the one-key patch into the stored bag; null deletes.
    const [error] = await updateDocumentExtractedDataAction({
      documentId,
      extractedData: { [key]: value || null },
    });

    if (error) {
      toast({
        message: "Failed to update field",
        type: "error",
        state: "active",
      });
      return;
    }

    toast({
      message: "Field updated successfully",
      type: "success",
      state: "active",
    });
    setEditingFieldKey(null);
    if (documentData) {
      setDocumentData({
        ...documentData,
        extractedData: mergeExtractedData(documentData.extractedData, {
          [key]: value || null,
        }),
      });
    }
  };

  // The current type's extraction field spec, for labels and edit rows.
  const extractionFieldSpec =
    definitions.find((definition) => definition.key === documentData?.type)
      ?.extractionFields ?? [];
  // Stored values whose keys the current spec no longer defines (field
  // deleted, or the document's type changed): shown read-only.
  const orphanedFieldKeys = Object.keys(
    documentData?.extractedData ?? {}
  ).filter((key) => !extractionFieldSpec.some((field) => field.key === key));

  // Other vendors the document's extracted company names resolve to — offered
  // as one-click corrections for an erroneous auto-assignment.
  const currentVendorId = documentData?.extractedVendors?.[0]?.vendor.id;
  const alternateVendors = matchVendorCandidates(
    documentData?.extractedVendorCandidates ?? [],
    vendorsList,
    { excludeVendorIds: [currentVendorId] }
  );

  return (
    <Modal
      title="Document Details"
      content={
        <Fragment>
          <div className="flex justify-center items-center">
            <PagedCloudImage
              src={src}
              alt={alt}
              width={1200}
              height={1200}
              sizes={sizes}
              className={`block w-full mx-auto ${className}`}
              containerClassName="w-full"
              alwaysShowControls
            />
          </div>

          {/* Document Information Section */}
          {isLoading ? (
            <div className="mt-6 space-y-4 border-t pt-4">
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            </div>
          ) : documentData ? (
            <div className="mt-6 space-y-4 border-t pt-4">
              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold">Created:</span>{" "}
                  <span className="text-base-content/70">
                    {getEstDateTime(new Date(documentData.createdAt))}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Updated:</span>{" "}
                  <span className="text-base-content/70">
                    {getEstDateTime(new Date(documentData.updatedAt))}
                  </span>
                </div>
              </div>

              {/* Extracted Document ID */}
              <div className="text-sm">
                <span className="font-semibold">Document ID:</span>{" "}
                {isEditingDocId && !viewOnly ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      className="input input-bordered input-sm"
                      value={editedDocId}
                      onChange={(e) => setEditedDocId(e.target.value)}
                      placeholder="Enter document ID"
                    />
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleSaveDocId}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setIsEditingDocId(false);
                        setEditedDocId(documentData.extractedDocumentId || "");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    {documentData.extractedDocumentId ? (
                      <span className="badge badge-outline">
                        {documentData.extractedDocumentId}
                      </span>
                    ) : (
                      <span className="text-base-content/50">Not set</span>
                    )}
                    {!viewOnly && (
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => setIsEditingDocId(true)}
                      >
                        Edit
                      </button>
                    )}
                  </span>
                )}
              </div>

              {/* Custom extracted fields (per the type's extraction spec) */}
              {(extractionFieldSpec.length > 0 ||
                orphanedFieldKeys.length > 0) && (
                <div className="text-sm space-y-2">
                  {extractionFieldSpec.map((field) => {
                    const value =
                      documentData.extractedData?.[field.key] ?? "";
                    const isEditing = editingFieldKey === field.key;
                    return (
                      <div key={field.key}>
                        <span className="font-semibold">{field.label}:</span>{" "}
                        {isEditing && !viewOnly ? (
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="text"
                              className="input input-bordered input-sm"
                              value={editedFieldValue}
                              onChange={(e) =>
                                setEditedFieldValue(e.target.value)
                              }
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleSaveField(field.key)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => setEditingFieldKey(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            {value ? (
                              <span className="badge badge-outline">
                                {value}
                              </span>
                            ) : (
                              <span className="text-base-content/50">
                                Not set
                              </span>
                            )}
                            {!viewOnly && (
                              <button
                                className="btn btn-xs btn-ghost"
                                onClick={() => {
                                  setEditingFieldKey(field.key);
                                  setEditedFieldValue(value);
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {orphanedFieldKeys.map((key) => (
                    <div
                      key={key}
                      className="text-base-content/50"
                      title="This field is no longer defined for the document's type"
                    >
                      <span className="font-semibold">
                        {humanizeFieldKey(key)}:
                      </span>{" "}
                      <span className="badge badge-ghost">
                        {documentData.extractedData?.[key]}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Associated Vendors */}
              <div className="text-sm">
                <span className="font-semibold">Vendor:</span>{" "}
                {isEditingVendor && !viewOnly ? (
                  <div className="mt-1">
                    <div className="flex items-end gap-2">
                      <select
                        className="select select-bordered select-sm flex-1 max-w-xs"
                        value={selectedVendorId || ""}
                        onChange={(e) => setSelectedVendorId(e.target.value || null)}
                        disabled={vendorsList.length === 0}
                      >
                        <option value="">{vendorsList.length === 0 ? "Loading vendors..." : "Select a vendor..."}</option>
                        {vendorsList
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(vendor => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </option>
                          ))}
                      </select>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSaveVendor}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => {
                          setIsEditingVendor(false);
                          // Reset to current vendor
                          if (documentData?.extractedVendors && documentData.extractedVendors.length > 0) {
                            setSelectedVendorId(documentData.extractedVendors[0].vendor.id);
                          } else {
                            setSelectedVendorId(null);
                          }
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    {documentData.extractedVendors && documentData.extractedVendors.length > 0 ? (
                      <>
                        <span className="badge badge-secondary">
                          {documentData.extractedVendors[0].vendor.name}
                        </span>
                        {documentData.extractedVendors[0].confidence < 1 && (
                          <span className="text-xs text-base-content/60">
                            {Math.round(documentData.extractedVendors[0].confidence * 100)}% confidence
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-base-content/50">No vendor assigned</span>
                    )}
                    {!viewOnly && projectId && (
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => setIsEditingVendor(true)}
                      >
                        {documentData.extractedVendors && documentData.extractedVendors.length > 0 ? "Change" : "Assign"}
                      </button>
                    )}
                  </span>
                )}
                {!isEditingVendor &&
                  !viewOnly &&
                  projectId &&
                  alternateVendors.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-base-content/60">
                      <span>Also found in document:</span>
                      {alternateVendors.map((alt) => (
                        <button
                          key={alt.vendorId}
                          type="button"
                          className="btn btn-xs btn-outline gap-1"
                          title={`Matched "${alt.extractedName}" — assign to this document`}
                          disabled={isAssigningVendor}
                          onClick={() => assignVendor(alt.vendorId)}
                        >
                          {alt.vendorName}
                          <span className="text-primary">&middot; Assign</span>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-4 border-t pt-4">
              <p className="text-center text-base-content/50">No document data available</p>
            </div>
          )}

          <form
            id="updateDocumentTypeForm"
            className="mt-4"
            onSubmit={handleSubmit}
          >
            <SelectInput
              label="Document Type:"
              name="documentType"
              options={(allowedDocumentTypes
                ? Object.values(allowedDocumentTypes)
                : // No explicit set: offer the types of the category this
                  // document currently belongs to (including custom types).
                  typesForCategory(
                    categoryForType(currentDocumentType ?? DocumentTypes.OTHER, {
                      hasProjectId: !!projectId,
                      defs: definitions,
                    }),
                    definitions
                  )
              ).map((s) => ({
                value: s,
                label: labelFor(s),
              }))}
              placeholder="Select Document Type"
              value={currentDocumentType}
              onChange={(e) => setCurrentDocumentType(e.target.value)}
              disabled={viewOnly}
            />
          </form>
        </Fragment>
      }
      buttons={[
        <button
          key="document-view-modal-cancel"
          className="btn btn-outline btn-error"
          onClick={onClose}
        >
          Cancel
        </button>,
        !viewOnly && (
          <button
            key="document-view-modal-update"
            type="submit"
            form="updateDocumentTypeForm"
            className="btn btn-primary"
            disabled={currentDocumentType === documentType}
          >
            Update Document Type
          </button>
        ),
      ].filter(Boolean)}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default DocumentViewModal;
